import type { FeatureBeatsFrame } from './FeatureBeats';
import { DEFAULT_TESTIMONIALS, type Testimonial } from './TestimonialsCarousel';
import addPeopleImg from './beats/add-people.png';
import savedForImg from './beats/saved-for.png';
import discoverImg from './beats/discover.png';
import newIdeasImg from './beats/new-ideas.png';

/**
 * The canonical homepage feature-stack content — the working-mom experience
 * that is now the DEFAULT homepage (Kate 2026-07-21; primary ICP = working
 * moms, domain.md). Lives in its own module so both LandingPage (which
 * renders it as the default beats section) AND the /welcome2 alias
 * (MomsLandingPage, an analytics-relabel wrapper) share ONE definition — no
 * circular import between the page components, no drift between the two
 * routes.
 *
 * The 4-beat story: set up once → boards → reminders → fresh ideas. The
 * first card's `typedExample` types itself inside the screenshot's freeform
 * textarea (TypedQuizNote).
 */
export const HOMEPAGE_STACK_FRAMES: FeatureBeatsFrame[] = [
  {
    image: addPeopleImg,
    w: 640,
    h: 1072,
    alt: 'Adding a person to Thea — choosing what she likes and a note about her',
    label: 'Tell Thea once.',
    body: 'Who they are, when their day is, what they love.',
    /* Typed INSIDE the screenshot's freeform textarea (TypedQuizNote) —
     * the screenshot's own line minus its last sentence (Kate 2026-07-21:
     * the health-scares sentence overflowed the typed patch). The patch
     * covers the baked-in text, so the shorter note is what reads. */
    typedExample:
      'She loves gardening, her dog and her 3 little grandkids. ' +
      'Drinks earl grey tea.',
  },
  {
    image: savedForImg,
    w: 640,
    h: 1169,
    alt: 'A saved board of gift ideas for Mom',
    label: 'Everyone gets a board.',
    body: 'Kids, grandparents, in-laws, your best friend.',
  },
  {
    image: newIdeasImg,
    w: 640,
    h: 1171,
    alt: "Thea's Events calendar with Mom's birthday and a stack of fresh gift picks for her",
    label: 'Reminders find you.',
    body: 'Before every birthday, every year — Thea does the remembering.',
  },
  {
    image: discoverImg,
    w: 640,
    h: 1222,
    alt: "Charlotte's board with a fresh feed of curated gift ideas",
    label: 'The ideas stay fresh.',
    body: "New finds land on their boards all year, so when the day comes, you're not searching. You're choosing.",
  },
];

/** The two-line emotional banner before the CTA. Celebrates the gifter
 *  first, positions Thea as the helper second — defuses the "outsourcing
 *  feels like cheating" worry (Kate 2026-07-21). */
export const HOMEPAGE_BANNER_HEADLINE: [string, string] = [
  'You love your people.',
  'Let it show.',
];

/** The hero subhead (education line under "Your gifting brain, all in one
 *  place."). */
export const HOMEPAGE_HERO_SUBHEAD =
  'Thea reminds you before the big days — with a board of gift ideas ready for everyone you love.';

/* Testimonials reordered so the carousel LEADS with the voices closest to
 * the working-mom cohort (just-because friend gifting, the treat-yourself
 * board) and the grandma quote rotates in last. Attribution-keyed so a
 * future addition to the shared set appends instead of breaking. */
const HOMEPAGE_TESTIMONIAL_ORDER = ['Anika S.', 'Priya D.', 'Ben R.', 'Susan'];
export const HOMEPAGE_TESTIMONIALS: Testimonial[] = [
  ...HOMEPAGE_TESTIMONIAL_ORDER.map((a) =>
    DEFAULT_TESTIMONIALS.find((t) => t.attribution === a),
  ),
  ...DEFAULT_TESTIMONIALS.filter(
    (t) => !HOMEPAGE_TESTIMONIAL_ORDER.includes(t.attribution),
  ),
].filter((t): t is Testimonial => Boolean(t));
